/* eslint-disable react-hooks/rules-of-hooks */
import {
  useInitDataQuery,
  useUserClearCacheMutation,
  useUserNotificationCountLazyQuery,
} from '@api/dto/gql-helpers.generated';
import { useUnit } from 'effector-react';
import type { PropsWithChildren, ReactElement } from 'react';
import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ToastContentProps } from 'react-toastify';
import { toast } from 'react-toastify';

import { Stack } from '@mui/material';

import { LoaderBackdrop } from '@components/loader/loaderBackdrop';

import { ROUTES } from '@constants/routes';
import { TOAST_MESSAGE_LIMIT } from '@constants/toast';

import { useDefaultFilters } from '@hooks/useDefaultFilters';

import { defineAccessFor } from '@system/acl';
import { METHODS } from '@system/acl/methods';

import { useEventBus } from '@utils/eventBus';
import {
  sessionStorageClear,
  sessionStorageSetItem,
} from '@utils/sessionStorage';

import { ToastContentComponent } from './components';
import { USER_CLEAR_CACHE_MESSAGE } from './constants';
import type { AppContextValue } from './context';
import { AppContext } from './context';
import { $confirmDialog, setConfirmDialog } from './store/confirmDialog.store';
import { $envIds } from './store/envIds.store';
import { $maniMenu, clearMenu, setMenu } from './store/mainMenu.store';
import {
  $notification,
  setNotificationCount,
  toggleNotificationDrawer,
} from './store/notification.store';

export const AppContextProvider = ({
  children,
}: PropsWithChildren): ReactElement => {
  const { publish, subscribe, unsubscribe } = useEventBus();
  const navigate = useNavigate();

  const { data: initData, refetch: refetchInitData } = useInitDataQuery({
    fetchPolicy: 'cache-first',
  });
  const flags = initData?.configuration.feature_flag_list.items;
  const [currentGdp, nextGdp] = [
    initData?.classifiers.gdp.currentGdp,
    initData?.classifiers.gdp.nextGdp,
  ];
  const me = initData?.users.me;
  const acl = defineAccessFor(me);
  const defaultFilters = useDefaultFilters(
    $envIds.defaultState.railwayAdministrationRzdId,
    $envIds.defaultState.passengerCarrierFpkId,
  );

  const [getUserNotificationCount] = useUserNotificationCountLazyQuery({
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (!me) return;

    void getUserNotificationCount({
      onCompleted: result => {
        setNotificationCount(
          result.notification.not_viewed_user_messages_count,
        );
      },
    });
  }, [me, getUserNotificationCount]);

  const [userClearCache] = useUserClearCacheMutation();

  const handleUserClearCacheAndLogout = useCallback(async () => {
    try {
      await userClearCache();
      sessionStorageClear();
      navigate(ROUTES.LOG_OUT);
      return true;
    } catch (err) {
      toast.error(
        `Ошибка завершения сессии для пользователя: ${me?.full_name}`,
      );
      return false;
    }
  }, [me, userClearCache, navigate]);

  useEffect(() => {
    if (me?.user_name) sessionStorageSetItem('userSession', me.user_name);

    if (me?.is_need_to_clear_cache) {
      setConfirmDialog({
        onConfirm: async () => await handleUserClearCacheAndLogout(),
        title: 'Внимание!',
        message: USER_CLEAR_CACHE_MESSAGE,
        buttonConfirm: { text: 'Продолжить' },
        disableCloseWithoutConfirm: true,
      });
    }
  }, [me, handleUserClearCacheAndLogout]);

  const handleViewAlertMessage = useCallback((message: string) => {
    setConfirmDialog({
      title: 'Уведомление',
      message,
      buttonClose: { text: 'Закрыть' },
    });
  }, []);

  if (defaultFilters.loading) return <LoaderBackdrop />;

  function toastContent<T>(
    content:
      | string
      | number
      | boolean
      | Iterable<React.ReactNode>
      | ((props: ToastContentProps<T>) => React.ReactNode)
      | ReactElement
      | null
      | undefined,
    dataTestId: string,
  ) {
    if (typeof content === 'string') {
      return content.length > TOAST_MESSAGE_LIMIT ? (
        <ToastContentComponent
          content={content.substring(0, TOAST_MESSAGE_LIMIT)}
          handleView={() => {
            handleViewAlertMessage(content);
          }}
          dataTestId={dataTestId}
        />
      ) : (
        <Stack data-testid={dataTestId}>{content}</Stack>
      );
    }
    return content;
  }

  const contextValue: AppContextValue = {
    refetchInitData,
    confirmDialog: {
      set: setConfirmDialog,
      use: () => useUnit($confirmDialog),
    },
    acl,
    me,
    envIds: $envIds.getState(),
    mainMenu: {
      set: setMenu,
      clear: clearMenu,
      use: () => useUnit($maniMenu),
    },
    flags,
    gdp: {
      current: currentGdp ?? null,
      next: nextGdp ?? null,
    },
    toast: {
      info: (content, options) => {
        return toast.info(
          toastContent(content, options?.dataTestId ?? 'toastInfo'),
          options,
        );
      },
      error: (content, options) => {
        return toast.error(
          toastContent(content, options?.dataTestId ?? 'toastError'),
          options,
        );
      },
      success: (content, options) => {
        return toast.success(
          toastContent(content, options?.dataTestId ?? 'toastSuccess'),
          options,
        );
      },
      dismiss: toast.dismiss,
    },
    notification: {
      subscribe,
      unsubscribe,
      publish,
    },
    notificationUI: {
      toggle: toggleNotificationDrawer,
      setCount: setNotificationCount,
      use: () => useUnit($notification),
    },
    isAdm: acl.can(METHODS.configuration.featureFlagListUpdate),
    defaultFilters,
    isReadOnlyWorker: Boolean(
      me?.roles.every(
        role => role.id === +$envIds.defaultState.readOnlyWorkerRoleId,
      ),
    ),
  };

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
};
